import moment from "moment";

// Under a paisa is rounding, not a real gap.
const TOLERANCE = 0.01;

// Checkout is the morning after the last night, so it is not itself a night.
export const nightsOf = (room) => {
    const nights = [];
    const checkout = moment.unix(room?.checkout_date).startOf("day");
    let night = moment.unix(room?.checkin_date).startOf("day");
    while (night.isBefore(checkout)) {
        nights.push(night.unix());
        night = night.clone().add(1, "day");
    }
    return nights;
};

export const everyNight = (rooms = []) =>
    rooms.flatMap((room, roomIndex) => nightsOf(room).map((date) => ({ roomIndex, date })));

// Changing this set is what justifies re-splitting.
export const nightsKey = (rooms = []) =>
    everyNight(rooms)
        .map(({ roomIndex, date }) => `${roomIndex}:${date}`)
        .join(",");

// Each night holds { amount, extra_bed_price, meal_plan_price }; amount is the room only.
export const NIGHT_FIELDS = [
    { field: "amount", label: "Room", placeholder: "0.00" },
    { field: "extra_bed_price", label: "Extra bed", placeholder: "0" },
    { field: "meal_plan_price", label: "Meal", placeholder: "0" },
];
const EXTRA_FIELDS = NIGHT_FIELDS.filter(({ field }) => field !== "amount");

export const cellOf = (entered, roomIndex, date) => entered?.[roomIndex]?.[date] || {};
export const numberOf = (value) => parseFloat(value) || 0;
export const round2 = (n) => Math.round(n * 100) / 100;
const extrasOf = (cell) => EXTRA_FIELDS.reduce((sum, { field }) => sum + numberOf(cell[field]), 0);

const sumOverNights = (rooms, entered, valueOf) =>
    round2(everyNight(rooms).reduce((sum, { roomIndex, date }) => sum + valueOf(cellOf(entered, roomIndex, date)), 0));

export const extrasTotal = (rooms = [], entered = {}) => sumOverNights(rooms, entered, extrasOf);

export const dayWiseTotal = (rooms = [], entered = {}) =>
    sumOverNights(rooms, entered, (cell) => numberOf(cell.amount) + extrasOf(cell));

// Splits roomTotal over the room amounts only and keeps the extras already entered. Remainder
// lands on the last night so the parts sum exactly. No positive roomTotal clears the amounts.
export const splitAcrossRooms = (rooms = [], roomTotal, entered = {}) => {
    const nights = everyNight(rooms);
    const splittable = nights.length > 0 && roomTotal > 0;
    const each = splittable ? Math.floor((roomTotal / nights.length) * 100) / 100 : 0;
    const split = {};
    nights.forEach(({ roomIndex, date }, i) => {
        let amount = "";
        if (splittable) {
            amount = i === nights.length - 1 ? round2(roomTotal - each * (nights.length - 1)) : each;
        }
        split[roomIndex] = {
            ...(split[roomIndex] || {}),
            [date]: { ...cellOf(entered, roomIndex, date), amount },
        };
    });
    return split;
};

// Blank is not an error; it is sent as 0.
export const isBadEntry = (raw) => {
    if (raw === undefined || raw === null || raw === "") return false;
    const value = parseFloat(raw);
    return !Number.isFinite(value) || value < 0;
};

const nightLabel = (rooms, roomIndex, date) => {
    const room = rooms[roomIndex];
    return `Room ${room?.room_no ?? roomIndex + 1}${
        room?.roomtype ? ` · ${room.roomtype}` : ""
    } on ${moment.unix(date).format("DD MMM YYYY")}`;
};

// Names the first bad night so submit fails loudly rather than dropping it.
export const dayWiseProblem = (rooms = [], entered = {}) => {
    for (const { roomIndex, date } of everyNight(rooms)) {
        const cell = cellOf(entered, roomIndex, date);
        if (numberOf(cell.amount) <= 0) {
            return `Day-wise rate for ${nightLabel(rooms, roomIndex, date)} must be greater than 0`;
        }
        const bad = EXTRA_FIELDS.find(({ field }) => isBadEntry(cell[field]));
        if (bad) return `${bad.label} for ${nightLabel(rooms, roomIndex, date)} must be 0 or more`;
    }
    return null;
};

// undefined where a room has no nights. Check dayWiseProblem first - a partial split is rejected.
export const dayWisePayload = (rooms = [], entered = {}) =>
    rooms.map((room, roomIndex) => {
        const nights = nightsOf(room);
        if (!nights.length) return undefined;
        return nights.map((date) => {
            const cell = cellOf(entered, roomIndex, date);
            return {
                date,
                amount: parseFloat(cell.amount),
                extra_bed_price: numberOf(cell.extra_bed_price),
                meal_plan_price: numberOf(cell.meal_plan_price),
            };
        });
    });

// Mirrors the booking engine's findMokshaRate (util-functions/bookings/update-with-rates.ts): an
// active weekdays row covering the date and listing its weekday, else an active seasonal row.
export const mokshaRateFor = (rates = [], date) => {
    if (!date) return null;
    const day = moment.unix(date).startOf("day");
    const weekday = day.format("dddd");
    const covers = (r) => r?.is_active === true && r.start_date <= day.unix() && r.end_date >= day.unix();
    return (
        rates.find((r) => covers(r) && r.type === "moksha_weekdays" && (r.weekdays || []).includes(weekday)) ||
        rates.find((r) => covers(r) && r.type === "moksha_seasonal") ||
        null
    );
};

// Ex-GST rate for one night of a room (check-in by default): its roomtype's Moksha rate on that
// date, else the booking engine rate.
export const rackRateOf = (room, offers = [], mokshaRates = {}, date = room?.checkin_date) => {
    const roomtypeId = room?.roomtype_id;
    const moksha = numberOf(mokshaRateFor(mokshaRates?.[roomtypeId], date)?.standard_rate);
    if (moksha > 0) return { rate: moksha, source: "moksha" };
    const offer = offers.find((o) => o?.roomtype_id === roomtypeId);
    return { rate: numberOf(offer?.standard_rate), source: "bke" };
};

// Stay total, each room-night priced on its own date as the booking engine does. Zero if any
// night has no rate, since a short prefill under-bills.
export const rackRateTotal = (rooms = [], offers = [], mokshaRates = {}) => {
    let total = 0;
    for (const room of rooms) {
        for (const date of nightsOf(room)) {
            const { rate } = rackRateOf(room, offers, mokshaRates, date);
            if (!rate) return 0;
            total += rate;
        }
    }
    return parseFloat(total.toFixed(2));
};

// One place for the rule, so the live warning and the submit check cannot disagree.
export const dayWiseMismatch = (rooms = [], entered = {}, specialPrice) => {
    const price = parseFloat(specialPrice);
    const total = dayWiseTotal(rooms, entered);
    const splittable = everyNight(rooms).length > 0;
    return { price, total, mismatch: splittable && price > 0 && Math.abs(total - price) >= TOLERANCE };
};

// Mirrors tariffOfInclusive in hotels-backend util-functions/bookings/moksha-payout.ts and
// TAX_RATES.ROOM: the slab is chosen on the inclusive room-night, 5% only strictly below 7,875.
const ROOM_GST_LOW = 0.05;
const ROOM_GST_HIGH = 0.18;
const ROOM_GST_THRESHOLD = 7500;
const INCLUSIVE_LOW_MAX = round2(ROOM_GST_THRESHOLD * (1 + ROOM_GST_LOW));

export const roomExGstOf = (inclusive, gstEnabled = true) => {
    const amount = numberOf(inclusive);
    if (!gstEnabled) return round2(amount);
    return round2(amount / (1 + (amount < INCLUSIVE_LOW_MAX ? ROOM_GST_LOW : ROOM_GST_HIGH)));
};

// Moksha may sell at most 25% below rack; below that the sale needs a confirmation.
const MIN_SHARE_OF_RACK = 0.75;
// Absorbs float noise, so a sale exactly on the floor (7,500 of 10,000) is never flagged.
const FLOOR_EPSILON = 1e-6;

// nightAmounts are inclusive room-only amounts, one per room-night. Null when there is nothing
// to compare or the stay is at or above the floor.
export const belowRackCheck = (nightAmounts = [], rackRate, gstEnabled = true) => {
    const rack = numberOf(rackRate);
    const nights = nightAmounts.map(numberOf).filter((n) => n > 0);
    if (!(rack > 0) || !nights.length) return null;
    const roomExGst = round2(nights.reduce((sum, n) => sum + roomExGstOf(n, gstEnabled), 0));
    if (rack * MIN_SHARE_OF_RACK - roomExGst <= FLOOR_EPSILON) return null;
    return { rack, roomExGst, percentBelow: round2((1 - roomExGst / rack) * 100) };
};

// Booking edit: the update does not re-split day-wise amounts, so the stored nights are only
// trusted while they still add up to the special price. Otherwise (price - stored extras) is
// averaged over the room-nights, and the average picks the slab.
export const storedRoomNights = (allRooms = [], specialPrice) => {
    // list_booking_details also returns cancelled rooms, which are no longer part of the stay.
    const rooms = allRooms.filter((room) => room?.status !== "cancelled");
    const price = numberOf(specialPrice);
    const stored = rooms.flatMap((room) => room?.day_wise_details || []);
    const storedTotal = round2(stored.reduce((sum, d) => sum + numberOf(d.amount) + extrasOf(d), 0));
    if (stored.length && (!(price > 0) || Math.abs(storedTotal - price) < TOLERANCE)) {
        return { method: "stored", nights: stored.map((d) => numberOf(d.amount)) };
    }
    if (!(price > 0)) return { method: "none", nights: [] };
    const nightCount = rooms.reduce((sum, room) => sum + nightsOf(room).length, 0);
    if (!nightCount) return { method: "none", nights: [] };
    const roomTotal = price - stored.reduce((sum, d) => sum + extrasOf(d), 0);
    return { method: "average", nights: Array(nightCount).fill(roomTotal / nightCount) };
};
